/**
 * Wrappers for `/api/builder/prompts` (Pydantic schemas in
 * `src/builder/schemas/prompts.py`).
 *
 * Three scopes:
 *  - `global` — applies to every report (e.g. `level1_system`).
 *  - `report` — per-draft override; `report_draft_id` set.
 *  - `client` — per-client override; `client_key` set.
 *
 * Saving a new `template_jinja` bumps `version` server-side so portals
 * pulling by name can detect changes.
 */

import { api } from "@/lib/api";

export type PromptScope = "global" | "report" | "client";

export interface PromptTemplate {
  id: string;
  name: string;
  scope: PromptScope;
  report_draft_id: string | null;
  client_key: string | null;
  template_jinja: string;
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PromptTemplateCreate {
  name: string;
  scope: PromptScope;
  report_draft_id?: string | null;
  client_key?: string | null;
  template_jinja: string;
  is_active?: boolean;
}

export interface PromptTemplateUpdate {
  template_jinja?: string;
  is_active?: boolean;
}

export interface PromptTemplateListResponse {
  items: PromptTemplate[];
  total: number;
}

export interface PromptListFilters {
  scope?: PromptScope;
  report_draft_id?: string | null;
  client_key?: string | null;
  only_active?: boolean;
}

export const promptsApi = {
  list: (filters: PromptListFilters = {}) =>
    api.get<PromptTemplateListResponse>("/api/builder/prompts", {
      query: {
        scope: filters.scope,
        report_draft_id: filters.report_draft_id ?? undefined,
        client_key: filters.client_key ?? undefined,
        only_active: filters.only_active,
      },
    }),
  /** Resolve by (name, scope, key) — server returns the active row. */
  lookup: (params: {
    name: string;
    scope: PromptScope;
    report_draft_id?: string | null;
    client_key?: string | null;
  }) =>
    api.get<PromptTemplate>("/api/builder/prompts/lookup", {
      query: {
        name: params.name,
        scope: params.scope,
        report_draft_id: params.report_draft_id ?? undefined,
        client_key: params.client_key ?? undefined,
      },
    }),
  get: (id: string) =>
    api.get<PromptTemplate>(`/api/builder/prompts/${id}`),
  create: (payload: PromptTemplateCreate) =>
    api.post<PromptTemplate>("/api/builder/prompts", payload),
  update: (id: string, payload: PromptTemplateUpdate) =>
    api.put<PromptTemplate>(`/api/builder/prompts/${id}`, payload),
  remove: (id: string) =>
    api.delete<void>(`/api/builder/prompts/${id}`),
};
